// /gold (M15): the Ledger as its Overview with the plan, flows and goals, then the Sell list,
// Craft for profit, Farm, Fishing and Market. Arithmetic only (SPEC §1.6): every number says how
// old its prices are or that there's no data.
import {
  MONEY_CATEGORIES,
  type GoldItem,
  type MarketRow,
  type PlanAction,
  type SellAction,
  type SellRow,
} from '@brimstone/shared';
import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  Chip,
  DataGrid,
  DataRow,
  EmptyState,
  KpiTile,
  PageLayout,
  Panel,
  PrimaryButton,
  SecondaryButton,
} from '../../components/ui';
import {
  COLOURS,
  useCraft,
  useFarm,
  useFishing,
  useFlows,
  useGoalMutation,
  useGoals,
  useMarket,
  usePlan,
  useSell,
} from '../../lib/gold';
import { formatMoney } from '../../lib/money';
import { useCharacters } from '../../lib/roster';
import { formatDate, formatPlayed } from '../../lib/time';
import { LedgerPage } from '../ledger/LedgerPage';

const VIEWS = [
  { key: 'overview', label: 'Overview' },
  { key: 'sell', label: 'Sell' },
  { key: 'craft', label: 'Craft' },
  { key: 'farm', label: 'Farm' },
  { key: 'fishing', label: 'Fishing' },
  { key: 'market', label: 'Market' },
] as const;
type View = (typeof VIEWS)[number]['key'];

const money = (c: number | null | undefined) =>
  c === null || c === undefined ? '—' : formatMoney(c);
const itemName = (i: GoldItem) => i.name ?? `Item ${i.itemId}`;
const asOf = (iso: string | null | undefined) =>
  iso ? `AH ${formatDate(iso).toUpperCase()}` : 'NO AH SCAN';

function Loading({ q, what }: { q: { isError: boolean }; what: string }) {
  return <EmptyState>{q.isError ? `Couldn’t load ${what}.` : 'Loading…'}</EmptyState>;
}

/** /gold, with `view` in the URL. */
export function GoldPage() {
  const [params, setParams] = useSearchParams();
  const view: View = VIEWS.find((v) => v.key === params.get('view'))?.key ?? 'overview';
  const set = (v: View) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === 'overview') next.delete('view');
        else next.set('view', v);
        return next;
      },
      { replace: true },
    );
  return (
    <div className="flex flex-col gap-5">
      <div
        role="group"
        aria-label="Gold views"
        className="-mx-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:px-0"
      >
        {VIEWS.map((v) => (
          <Chip key={v.key} pressed={view === v.key} onClick={() => set(v.key)}>
            {v.label}
          </Chip>
        ))}
      </div>
      {view === 'overview' && (
        <LedgerPage
          main={<PlanPanel />}
          side={
            <>
              <GoalsPanel />
              <FlowsPanel />
            </>
          }
        />
      )}
      {view === 'sell' && <SellView />}
      {view === 'craft' && <CraftView />}
      {view === 'farm' && <FarmView />}
      {view === 'fishing' && <FishingView />}
      {view === 'market' && <MarketView />}
    </div>
  );
}

// ── Overview: plan, flows, goals ─────────────────────────────────────────────

function actionLine(a: PlanAction): ReactNode {
  switch (a.kind) {
    case 'auction':
      return (
        <>
          Post {a.stacks} {a.stacks === 1 ? 'stack' : 'stacks'} on the AH{' '}
          <span className="font-mono text-muted">≈ {formatMoney(a.valueCopper, true)}</span>
        </>
      );
    case 'vendor':
      return (
        <>
          Sell {a.stacks} {a.stacks === 1 ? 'stack' : 'stacks'} to a vendor{' '}
          <span className="font-mono text-muted">{formatMoney(a.valueCopper)}</span>
        </>
      );
    case 'mail':
      return (
        <>
          Mail {a.stacks} {a.stacks === 1 ? 'stack' : 'stacks'} of materials to{' '}
          <Link to={`/book/${a.toId}`}>{a.to}</Link>
        </>
      );
    case 'craft':
      return (
        <>
          Craft {a.times} × {a.recipe}{' '}
          <span className="font-mono text-muted">+{formatMoney(a.profitCopper)}</span>
        </>
      );
    case 'cooldown':
      return (
        <>
          {a.name} <span className="font-mono text-accent">READY</span>
        </>
      );
  }
}

function PlanPanel() {
  const q = usePlan();
  return (
    <Panel title="Plan" meta="NEXT STEPS FOR GOLD">
      {!q.data ? (
        <Loading q={q} what="the plan" />
      ) : q.data.characters.length === 0 ? (
        <EmptyState>Nothing to do yet — it fills in from bags, prices and recipes.</EmptyState>
      ) : (
        <ul className="m-0 list-none p-0">
          {q.data.characters.map((c) => (
            <li
              key={c.id}
              className="flex flex-col gap-1 border-b border-line-soft px-4 py-3 last:border-b-0"
            >
              <Link to={`/book/${c.id}`} className="text-name">
                {c.name}
              </Link>
              <ul className="m-0 list-none p-0 text-secondary">
                {c.actions.map((a, i) => (
                  <li key={i}>{actionLine(a)}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

const CATEGORY_LABELS: Record<string, string> = {
  loot: 'Loot',
  quest: 'Quests',
  merchant: 'Merchants',
  repair: 'Repairs',
  auction: 'Auctions',
  mail: 'Mail',
  trainer: 'Training',
  taxi: 'Flights',
  trade: 'Trades',
  other: 'Other',
};

function FlowsPanel() {
  const q = useFlows();
  const week = q.data?.weeks.at(-1);
  return (
    <Panel
      title="This week’s gold"
      level="side"
      meta={week ? `FROM ${formatDate(week.startAt).toUpperCase()}` : undefined}
    >
      {!q.data ? (
        <Loading q={q} what="the flows" />
      ) : !q.data.hasData ? (
        <EmptyState>
          No data — the Brimstone addon 0.4.0 counts gold from its first session.
        </EmptyState>
      ) : (
        <>
          <ul className="m-0 list-none px-4 py-2 text-compact">
            {MONEY_CATEGORIES.filter((c) => week?.categories[c]).map((c) => {
              const m = week!.categories[c]!;
              return (
                <li
                  key={c}
                  className="flex justify-between gap-3 border-b border-line-soft py-1.5 last:border-b-0"
                >
                  <span>{CATEGORY_LABELS[c]}</span>
                  <span className="font-mono">
                    {m.gain > 0 && <span>+{formatMoney(m.gain, true)}</span>}
                    {m.gain > 0 && m.spend > 0 && ' '}
                    {m.spend > 0 && (
                      <span className="text-muted">−{formatMoney(m.spend, true)}</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          {q.data.characters.length > 0 && (
            <ul className="m-0 list-none border-t border-line px-4 py-2 text-compact">
              {q.data.characters.map((c) => (
                <li key={c.id} className="flex justify-between gap-3 py-1">
                  <span>{c.name}</span>
                  <span className="font-mono text-muted">
                    {c.perHourCopper !== null
                      ? `${formatMoney(c.perHourCopper, true)}/h`
                      : formatPlayed(c.seconds)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Panel>
  );
}

function GoalsPanel() {
  const q = useGoals();
  const characters = useCharacters();
  const mutate = useGoalMutation();
  const [label, setLabel] = useState('');
  const [gold, setGold] = useState('');
  const [who, setWho] = useState('');
  /** The goal being edited, if any: the form saves over it. */
  const [editing, setEditing] = useState<string | null>(null);
  const save = () => {
    const target = Math.round(Number(gold) * 10_000);
    if (!label.trim() || !(target > 0)) return;
    mutate.mutate(
      {
        id: editing ?? undefined,
        goal: { characterId: who || null, label: label.trim(), targetCopper: target },
      },
      {
        onSuccess: () => {
          setLabel('');
          setGold('');
          setEditing(null);
        },
      },
    );
  };
  const input = 'min-h-11 min-w-0 border border-line bg-bg px-3 text-compact';
  return (
    <Panel title="Goals" level="side">
      {!q.data ? (
        <Loading q={q} what="the goals" />
      ) : q.data.goals.length === 0 ? (
        <EmptyState>No goals — set one below, say riding at 40.</EmptyState>
      ) : (
        <ul className="m-0 list-none px-4 py-2 text-compact">
          {q.data.goals.map((g) => {
            const share = g.haveCopper !== null ? Math.min(1, g.haveCopper / g.targetCopper) : 0;
            return (
              <li
                key={g.id}
                className="flex flex-col gap-1 border-b border-line-soft py-2 last:border-b-0"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate">
                    {g.label} <span className="text-muted">{g.characterName ?? 'account'}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(g.id);
                      setLabel(g.label);
                      setGold(String(g.targetCopper / 10_000));
                      setWho(g.characterId ?? '');
                    }}
                    aria-label={`Edit ${g.label}`}
                    className="min-h-11 min-w-11 cursor-pointer border-0 bg-transparent font-mono text-muted hover:text-text"
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    onClick={() => mutate.mutate({ id: g.id, delete: true })}
                    aria-label={`Delete ${g.label}`}
                    className="min-h-11 min-w-11 cursor-pointer border-0 bg-transparent font-mono text-muted hover:text-text"
                  >
                    ✕
                  </button>
                </div>
                <div className="h-1 bg-line">
                  <div className="h-1 bg-accent" style={{ width: `${share * 100}%` }} />
                </div>
                <span className="font-mono text-label text-muted">
                  {money(g.haveCopper)} / {formatMoney(g.targetCopper, true)} ·{' '}
                  {g.reached ? (
                    <span className="text-accent">REACHED</span>
                  ) : g.etaAt ? (
                    `about ${formatDate(g.etaAt)}`
                  ) : (
                    'NO PACE YET'
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <form
        className="flex flex-col gap-2 border-t border-line px-4 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <input
          aria-label="Goal"
          placeholder="Goal"
          value={label}
          maxLength={60}
          onChange={(e) => setLabel(e.target.value)}
          className={input}
        />
        <div className="flex gap-2">
          <input
            aria-label="Gold"
            placeholder="Gold"
            inputMode="decimal"
            value={gold}
            onChange={(e) => setGold(e.target.value)}
            className={`${input} w-24`}
          />
          <select
            aria-label="For"
            value={who}
            onChange={(e) => setWho(e.target.value)}
            className={`${input} flex-1`}
          >
            <option value="">Account</option>
            {(characters.data?.characters ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <PrimaryButton type="submit" disabled={mutate.isPending}>
          {editing ? 'Save goal' : 'Add goal'}
        </PrimaryButton>
        {editing && (
          <SecondaryButton
            type="button"
            onClick={() => {
              setEditing(null);
              setLabel('');
              setGold('');
            }}
          >
            Cancel
          </SecondaryButton>
        )}
      </form>
    </Panel>
  );
}

// ── Sell ─────────────────────────────────────────────────────────────────────

const ACTIONS: Record<SellAction, { word: string; className: string }> = {
  mail: { word: 'MAIL', className: 'text-accent' },
  auction: { word: 'AUCTION', className: 'text-accent' },
  vendor: { word: 'VENDOR', className: 'text-text' },
  disenchant: { word: 'DISENCHANT', className: 'text-text' },
  keep: { word: 'KEEP', className: 'text-muted' },
  nothing: { word: 'NO VALUE', className: 'text-muted' },
};
const SELL_COLUMNS = 'minmax(0,1.4fr) minmax(0,1fr) 64px 100px 110px minmax(0,1.3fr)';

function sellDetail(r: SellRow): ReactNode {
  switch (r.action) {
    case 'mail':
    case 'keep':
      return (
        <>
          {r.action === 'mail' ? 'to ' : ''}
          {r.crafter && <Link to={`/book/${r.crafter.id}`}>{r.crafter.name}</Link>}
          {r.usedBy.length > 0 && <span className="text-muted"> · {r.usedBy.join(', ')}</span>}
        </>
      );
    case 'auction':
      return (
        <>
          {money(r.unitCopper)} each
          {r.listed !== null && <span className="text-muted"> · {r.listed} listed</span>}
          {r.crowded && <span className="font-mono text-tag text-muted"> CROWDED</span>}
        </>
      );
    case 'disenchant':
      return <span className="text-muted">{r.enchanters.join(', ')}</span>;
    default:
      return null;
  }
}

function SellView() {
  const q = useSell();
  const [action, setAction] = useState<SellAction | null>(null);
  const [who, setWho] = useState<string | null>(null);
  const rows = (q.data?.rows ?? []).filter(
    (r) => (!action || r.action === action) && (!who || r.characterId === who),
  );
  const characters = [
    ...new Map((q.data?.rows ?? []).map((r) => [r.characterId, r.characterName])),
  ];
  const t = q.data?.totals ?? {};
  return (
    <PageLayout
      kpis={
        <>
          <KpiTile
            label="To the AH"
            value={t.auction ? formatMoney(t.auction.copper, true) : '—'}
          />
          <KpiTile label="To vendors" value={t.vendor ? formatMoney(t.vendor.copper, true) : '—'} />
          <KpiTile label="Stacks to mail" value={t.mail?.stacks ?? 0} />
          <KpiTile label="To disenchant" value={t.disenchant?.stacks ?? 0} />
        </>
      }
      main={
        <Panel title="Sell list" meta={asOf(q.data?.pricesAsOf)}>
          <div className="flex flex-col gap-3 border-b border-line px-4 py-3">
            <div className="-mx-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:px-0">
              <Chip pressed={action === null} onClick={() => setAction(null)}>
                All
              </Chip>
              {(Object.keys(ACTIONS) as SellAction[])
                .filter((a) => t[a])
                .map((a) => (
                  <Chip
                    key={a}
                    pressed={action === a}
                    count={t[a]!.stacks}
                    onClick={() => setAction(action === a ? null : a)}
                  >
                    {ACTIONS[a].word.charAt(0) + ACTIONS[a].word.slice(1).toLowerCase()}
                  </Chip>
                ))}
            </div>
            {characters.length > 1 && (
              <div className="-mx-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:flex-wrap sm:px-0">
                <Chip pressed={who === null} onClick={() => setWho(null)}>
                  Everyone
                </Chip>
                {characters.map(([id, name]) => (
                  <Chip
                    key={id}
                    pressed={who === id}
                    onClick={() => setWho(who === id ? null : id)}
                  >
                    {name}
                  </Chip>
                ))}
              </div>
            )}
          </div>
          {!q.data ? (
            <Loading q={q} what="the sell list" />
          ) : rows.length === 0 ? (
            <EmptyState>Nothing held yet — bags, bank and mail come from Alts Forever.</EmptyState>
          ) : (
            <DataGrid
              label="Sell list"
              columns={SELL_COLUMNS}
              header={['Item', 'Held by', 'Count', 'Action', 'Value', '']}
            >
              {rows.map((r) => (
                <DataRow key={`${r.characterId}${r.location}${r.itemId}`} columns={SELL_COLUMNS}>
                  <span role="cell" className={`min-w-0 truncate ${r.name ? '' : 'text-muted'}`}>
                    {itemName(r)}
                  </span>
                  <span role="cell" className="truncate text-secondary">
                    {r.characterName} <span className="text-muted">{r.location}</span>
                  </span>
                  <span role="cell" className="font-mono text-secondary">
                    ×{r.count}
                  </span>
                  <span
                    role="cell"
                    className={`font-mono text-label font-semibold ${ACTIONS[r.action].className}`}
                  >
                    {ACTIONS[r.action].word}
                  </span>
                  <span role="cell" className="font-mono text-secondary">
                    {r.totalCopper ? formatMoney(r.totalCopper) : '—'}
                  </span>
                  <span role="cell" className="truncate text-secondary">
                    {sellDetail(r)}
                  </span>
                </DataRow>
              ))}
            </DataGrid>
          )}
        </Panel>
      }
      side={
        <Panel title="How it decides" level="side">
          <p className="m-0 px-4 py-3 text-secondary text-muted">
            Materials a roster crafter’s known recipes use go to that crafter (same faction only).
            The rest goes to the AH when it pays a quarter more than a vendor after its 5 % cut, at
            the median of the last week’s lowest prices; else to a vendor. Bound gear goes to an
            enchanter. Nothing is sent anywhere: it’s a list to work through in game.
          </p>
        </Panel>
      }
    />
  );
}

// ── Craft ────────────────────────────────────────────────────────────────────

const CRAFT_COLUMNS = 'minmax(0,1.6fr) 84px 110px 110px 110px 72px';

function CraftView() {
  const q = useCraft();
  return (
    <div className="flex flex-col gap-5">
      {!q.data ? (
        <Panel title="Craft for profit">
          <Loading q={q} what="the recipes" />
        </Panel>
      ) : !q.data.hasRecipes ? (
        <Panel title="Craft for profit">
          <EmptyState>No data — the client build’s recipes arrive with its map atlas.</EmptyState>
        </Panel>
      ) : q.data.professions.length === 0 ? (
        <Panel title="Craft for profit">
          <EmptyState>
            No known recipes yet — Alts Forever records them when a profession window is opened.
          </EmptyState>
        </Panel>
      ) : (
        q.data.professions.map((p) => (
          <Panel
            key={`${p.characterId}${p.profession}`}
            title={`${p.characterName} · ${p.profession} ${p.rank}`}
            meta={asOf(q.data.pricesAsOf)}
          >
            {p.route && (
              <div className="border-b border-line px-4 py-3 text-secondary">
                <span className="text-label uppercase tracking-label text-muted">
                  To {p.nextTier}{' '}
                </span>
                {p.route.steps.length === 0 ? (
                  <span className="text-muted">no known recipe gives skill from here</span>
                ) : (
                  <>
                    {p.route.steps.map((s, i) => (
                      <span key={i}>
                        {i > 0 && ', then '}
                        {s.crafts} × {s.recipe}{' '}
                        <span className="font-mono text-muted">
                          {s.from}→{s.to}
                        </span>
                      </span>
                    ))}
                    <span className="font-mono"> ≈ {money(p.route.costCopper)}</span>
                    {p.route.stuckAt !== null && (
                      <span className="text-muted">
                        {' '}
                        · known recipes stop giving skill at {p.route.stuckAt}
                      </span>
                    )}
                  </>
                )}
              </div>
            )}
            <DataGrid
              label={`${p.characterName}’s ${p.profession}`}
              columns={CRAFT_COLUMNS}
              header={['Recipe', 'Colour', 'Makes', 'Costs', 'Profit', 'Margin']}
            >
              {p.recipes.map((r) => (
                <DataRow key={r.spellId} columns={CRAFT_COLUMNS}>
                  <span role="cell" className="min-w-0 truncate">
                    {r.recipe}
                  </span>
                  <span
                    role="cell"
                    className={`font-mono text-label font-semibold ${COLOURS[r.colour].className}`}
                  >
                    {COLOURS[r.colour].word}
                  </span>
                  <span role="cell" className="font-mono text-secondary">
                    {r.makes ? (
                      money(r.makes.valueCopper)
                    ) : (
                      <span className="text-muted">enchant</span>
                    )}
                  </span>
                  <span role="cell" className="font-mono text-secondary">
                    {money(r.costCopper)}
                  </span>
                  <span
                    role="cell"
                    className={`font-mono text-secondary ${r.profitCopper !== null && r.profitCopper > 0 ? 'text-accent' : ''}`}
                  >
                    {money(r.profitCopper)}
                  </span>
                  <span role="cell" className="font-mono text-secondary text-muted">
                    {r.margin !== null ? `${Math.round(r.margin * 100)}%` : '—'}
                  </span>
                </DataRow>
              ))}
            </DataGrid>
          </Panel>
        ))
      )}
    </div>
  );
}

// ── Farm and fishing ─────────────────────────────────────────────────────────

const FARM_COLUMNS = 'minmax(0,1fr) minmax(0,1.2fr) 80px 100px 100px 100px minmax(0,1.4fr)';

function FarmView() {
  const q = useFarm();
  const d = q.data;
  return (
    <PageLayout
      main={
        <>
          <Panel
            title="Measured"
            meta={d?.activityAsOf ? `AS OF ${formatDate(d.activityAsOf).toUpperCase()}` : 'NO DATA'}
          >
            {!d ? (
              <Loading q={q} what="the farming" />
            ) : d.measured.length === 0 ? (
              <EmptyState>
                No data — the Brimstone addon 0.4.0 counts time, gold and loot per zone.
              </EmptyState>
            ) : (
              <DataGrid
                label="Measured"
                columns={FARM_COLUMNS}
                header={['Character', 'Zone', 'Time', 'Gold', 'Loot', 'Per hour', 'Best loot']}
              >
                {d.measured.map((z) => (
                  <DataRow key={`${z.characterId}${z.zone}`} columns={FARM_COLUMNS}>
                    <span role="cell">{z.characterName}</span>
                    <span role="cell" className="truncate">
                      {z.zone}
                    </span>
                    <span role="cell" className="font-mono text-secondary">
                      {formatPlayed(z.seconds)}
                    </span>
                    <span role="cell" className="font-mono text-secondary">
                      {formatMoney(z.goldCopper, true)}
                    </span>
                    <span role="cell" className="font-mono text-secondary">
                      {formatMoney(z.lootCopper, true)}
                    </span>
                    <span role="cell" className="font-mono text-accent">
                      {formatMoney(z.perHourCopper, true)}
                    </span>
                    <span role="cell" className="truncate text-secondary text-muted">
                      {z.top.map((t) => `${itemName(t)} ×${t.count}`).join(', ')}
                    </span>
                  </DataRow>
                ))}
              </DataGrid>
            )}
          </Panel>
          <Panel title="Gathering" meta="ESTIMATE">
            {!d ? (
              <Loading q={q} what="the gathering" />
            ) : d.gathering.length === 0 ? (
              <EmptyState>
                No data — needs a gatherer’s skill and ForeverMapNotes’ nodes.
              </EmptyState>
            ) : (
              <ul className="m-0 list-none p-0">
                {d.gathering.map((g) => (
                  <li
                    key={`${g.characterId}${g.profession}${g.mapId}`}
                    className="flex flex-col gap-1 border-b border-line-soft px-4 py-3 last:border-b-0"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                      <span>
                        {g.characterName}{' '}
                        <span className="text-muted">
                          {g.profession} {g.rank}
                        </span>{' '}
                        · {g.zone}
                      </span>
                      <span className="font-mono">
                        ≈ {formatMoney(g.roundCopper, true)} a round
                      </span>
                    </div>
                    <span className="text-secondary text-muted">
                      {g.nodes
                        .map(
                          (n) =>
                            `${n.spots} × ${itemName(n.item)}${n.unitCopper !== null ? ` (${formatMoney(n.unitCopper)})` : ''}`,
                        )
                        .join(', ')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {d && (
              <p className="m-0 border-t border-line px-4 py-3 text-secondary text-muted">
                Every node spot once at {d.yieldPerNode} items each, at what the AH pays after its
                cut. Spots aren’t respawns: measured rates replace this once the addon has counted a
                few evenings.
              </p>
            )}
          </Panel>
        </>
      }
    />
  );
}

function FishingView() {
  const q = useFishing();
  const d = q.data;
  return (
    <PageLayout
      main={
        <Panel title="Fishing" meta={asOf(d?.pricesAsOf)}>
          {!d ? (
            <Loading q={q} what="the fishing" />
          ) : d.characters.length === 0 ? (
            <EmptyState>
              Nobody fishes yet — catches appear after a logout with the Brimstone addon 0.4.0.
            </EmptyState>
          ) : (
            <ul className="m-0 list-none p-0">
              {d.characters.map((c) => (
                <li
                  key={c.characterId}
                  className="flex flex-col gap-1 border-b border-line-soft px-4 py-3 last:border-b-0"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <Link to={`/book/${c.characterId}`} className="text-name">
                      {c.characterName}
                    </Link>
                    <span className="font-mono text-secondary">
                      {c.rank !== null ? `${c.rank}/${c.max ?? '—'}` : 'NO SKILL DATA'} ·{' '}
                      {c.catches} catches · {formatMoney(c.valueCopper, true)}
                    </span>
                  </div>
                  {c.zones.map((z) => (
                    <span key={z.zone} className="text-secondary">
                      {z.zone}{' '}
                      <span className="font-mono text-muted">
                        {z.catches} · {formatMoney(z.valueCopper, true)}
                      </span>{' '}
                      <span className="text-muted">
                        {z.top.map((t) => `${itemName(t)} ×${t.count}`).join(', ')}
                      </span>
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      }
      side={
        <Panel title="Pools" level="side" meta="FOREVERMAPNOTES">
          {!d ? (
            <Loading q={q} what="the pools" />
          ) : d.pools.length === 0 ? (
            <EmptyState>No data — ForeverMapNotes’ pools arrive with its files.</EmptyState>
          ) : (
            <ul className="m-0 list-none px-4 py-2 text-compact">
              {d.pools.map((z) => (
                <li
                  key={z.mapId}
                  className="flex flex-col gap-0.5 border-b border-line-soft py-2 last:border-b-0"
                >
                  <span>{z.zone}</span>
                  {z.pools.map((p) => (
                    <span key={p.label} className="text-secondary text-muted">
                      {p.label} ×{p.spots}
                      {p.unitCopper !== null && (
                        <span className="font-mono"> · {formatMoney(p.unitCopper)}</span>
                      )}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      }
    />
  );
}

// ── Market ───────────────────────────────────────────────────────────────────

const MARKET_COLUMNS = 'minmax(0,1.6fr) 100px 100px 80px 80px 70px';

function MarketList({ title, rows }: { title: string; rows: MarketRow[] }) {
  return (
    <Panel title={title} meta={`${rows.length}`}>
      {rows.length === 0 ? (
        <EmptyState>Nothing moved that far.</EmptyState>
      ) : (
        <DataGrid
          label={title}
          columns={MARKET_COLUMNS}
          header={['Item', 'Now', 'Median', 'Change', 'Listed', 'Held']}
        >
          {rows.map((r) => (
            <DataRow key={r.itemId} columns={MARKET_COLUMNS}>
              <span role="cell" className={`truncate ${r.name ? '' : 'text-muted'}`}>
                {itemName(r)}
              </span>
              <span role="cell" className="font-mono text-secondary">
                {formatMoney(r.latestCopper)}
              </span>
              <span role="cell" className="font-mono text-secondary text-muted">
                {formatMoney(r.medianCopper)}
              </span>
              <span
                role="cell"
                className={`font-mono text-secondary ${r.change > 0 ? 'text-accent' : ''}`}
              >
                {r.change > 0 ? '+' : '−'}
                {Math.abs(Math.round(r.change * 100))}%
              </span>
              <span role="cell" className="font-mono text-secondary text-muted">
                {r.available ?? '—'}
              </span>
              <span role="cell" className="font-mono text-secondary">
                {r.held || '—'}
              </span>
            </DataRow>
          ))}
        </DataGrid>
      )}
    </Panel>
  );
}

function MarketView() {
  const q = useMarket();
  const d = q.data;
  return (
    <PageLayout
      main={
        !d ? (
          <Panel title="Market">
            <Loading q={q} what="the market" />
          </Panel>
        ) : !d.asOf ? (
          <Panel title="Market">
            <EmptyState>No data — scan the auction house with Auctionator.</EmptyState>
          </Panel>
        ) : (
          <>
            <MarketList title="Up: sell what you hold" rows={d.rising} />
            <MarketList title="Down: buy what you need" rows={d.falling} />
          </>
        )
      }
      side={
        <Panel title="About" level="side" meta={d?.asOf ? asOf(d.asOf) : undefined}>
          <p className="m-0 px-4 py-3 text-secondary text-muted">
            Each item’s lowest price on its newest scan day against the median of its last{' '}
            {d?.days ?? 14} days’ lows, when it moved 15 % or more over at least three scan days.
            Held items first. Listings aren’t sales: the AH doesn’t say what sold.
          </p>
          <div className="border-t border-line px-4 py-3">
            <SecondaryButton type="button" onClick={() => void q.refetch()}>
              Refresh
            </SecondaryButton>
          </div>
        </Panel>
      }
    />
  );
}
